import { isArray } from 'lodash'

/** Single rule or compound group (logic + rules). */
export interface Condition {
  operator?: string
  value?: any
  logic?: 'AND' | 'OR'
  rules?: Condition[]
}

/**
 * Evaluates a display condition against the current answers.
 * Ported from PHP backend spec and Admin logic.
 */
export const evaluateCondition = (
  condition: Condition | null | undefined,
  allAnswers: Record<string, any>,
  parentQuestionId: string | number,
): boolean => {
  if (!condition) return false
  // Match PHP shouldDisplay(): empty display_conditions object/array → not displayed.
  // {} and [] are truthy in JS so the !condition guard above misses them.
  if (
    typeof condition === 'object' &&
    Object.keys(condition as object).length === 0
  )
    return false

  // Case: Compound condition { logic, rules }
  if (condition.logic && Array.isArray(condition.rules)) {
    if (condition.rules.length === 0) return true
    if (condition.logic === 'AND') {
      return condition.rules.every((rule) =>
        evaluateCondition(rule, allAnswers, parentQuestionId),
      )
    } else {
      return condition.rules.some((rule) =>
        evaluateCondition(rule, allAnswers, parentQuestionId),
      )
    }
  }

  // Case: Single rule { operator, value }
  // Support legacy field names (comparison/answer_value) returned by some API versions
  const operator = (condition as any).operator ?? (condition as any).comparison
  const comparisonValue =
    (condition as any).value ?? (condition as any).answer_value

  // Get the answer for the parent question
  // In the web app, answer state might be complex (array of objects for checkbox/radio)
  const answerData = allAnswers[parentQuestionId]

  // Extract simple values from the answer data
  let answerValue: any = null
  if (answerData !== undefined && answerData !== null) {
    if (isArray(answerData)) {
      // Checkbox or Multiple Radio
      // Some parts of the web app use [{ value: '...' }, ...]
      answerValue = answerData.map((a: any) =>
        a && typeof a === 'object' ? a.value : a,
      )
    } else if (typeof answerData === 'object') {
      answerValue = answerData.value
    } else {
      answerValue = answerData
    }
  }

  const isAnswerEmpty =
    answerValue === null ||
    answerValue === undefined ||
    String(answerValue).trim() === '' ||
    (isArray(answerValue) && answerValue.length === 0)

  if (operator !== 'is_empty' && isAnswerEmpty) {
    return false
  }

  // Scalar operators (gt/gte/lt/lte/contains/not_contains) operate on the first value only,
  // matching PHP evaluateRule()'s $firstValue after null-filtering.
  // PHP: array_values(array_filter($values, fn($v) => $v !== null))[0]
  // answerValue[0] is wrong when the first element is null (e.g. unticked leading checkbox option).
  const scalarAnswerValue = isArray(answerValue)
    ? answerValue.find((v) => v !== null && v !== undefined) ?? null
    : answerValue

  switch (operator) {
    case 'is':
    case 'equals':
      if (isArray(answerValue)) {
        return answerValue.some(
          (v) => String(v).trim() == String(comparisonValue).trim(),
        )
      }
      return String(answerValue).trim() == String(comparisonValue).trim()
    case 'not_is':
    case 'not_equals':
      if (isArray(answerValue)) {
        return !answerValue.some(
          (v) => String(v).trim() == String(comparisonValue).trim(),
        )
      }
      return String(answerValue).trim() != String(comparisonValue).trim()
    case 'in': {
      const conditionValue =
        comparisonValue !== undefined
          ? comparisonValue
          : (condition as any).answer_value
      const conditionArray = isArray(conditionValue)
        ? conditionValue.map((v) => String(v).trim())
        : [String(conditionValue).trim()]
      if (isArray(answerValue)) {
        return answerValue.some((v) =>
          conditionArray.includes(String(v).trim()),
        )
      }
      return conditionArray.includes(String(answerValue).trim())
    }
    case 'not_in': {
      const conditionValue =
        comparisonValue !== undefined
          ? comparisonValue
          : (condition as any).answer_value
      const conditionArray = isArray(conditionValue)
        ? conditionValue.map((v) => String(v).trim())
        : [String(conditionValue).trim()]
      if (isArray(answerValue)) {
        return !answerValue.some((v) =>
          conditionArray.includes(String(v).trim()),
        )
      }
      return !conditionArray.includes(String(answerValue).trim())
    }
    case 'contains':
      return (
        scalarAnswerValue !== null &&
        scalarAnswerValue !== undefined &&
        String(scalarAnswerValue)
          .trim()
          .includes(String(comparisonValue).trim())
      )
    case 'not_contains':
      return (
        scalarAnswerValue === null ||
        scalarAnswerValue === undefined ||
        !String(scalarAnswerValue)
          .trim()
          .includes(String(comparisonValue).trim())
      )
    case 'gt':
      return Number(scalarAnswerValue) > Number(comparisonValue)
    case 'gte':
      return Number(scalarAnswerValue) >= Number(comparisonValue)
    case 'lt':
      return Number(scalarAnswerValue) < Number(comparisonValue)
    case 'lte':
      return Number(scalarAnswerValue) <= Number(comparisonValue)
    case 'between':
      if (isArray(comparisonValue) && comparisonValue.length === 2) {
        const val = Number(scalarAnswerValue)
        return (
          val >= Number(comparisonValue[0]) && val <= Number(comparisonValue[1])
        )
      }
      return false
    case 'regex':
      try {
        const re = new RegExp(comparisonValue)
        return re.test(String(scalarAnswerValue))
      } catch {
        return false
      }
    case 'is_empty':
      return isAnswerEmpty
    case 'is_not_empty':
      return !isAnswerEmpty
    case 'count_eq':
      return (
        isArray(answerValue) && answerValue.length === Number(comparisonValue)
      )
    case 'count_gt':
      return (
        isArray(answerValue) && answerValue.length > Number(comparisonValue)
      )
    case 'count_lt':
      return (
        isArray(answerValue) && answerValue.length < Number(comparisonValue)
      )
    case 'count_gte':
      return (
        isArray(answerValue) && answerValue.length >= Number(comparisonValue)
      )
    case 'count_lte':
      return (
        isArray(answerValue) && answerValue.length <= Number(comparisonValue)
      )
    default:
      return true
  }
}
